import React, { useState, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Download, LoaderCircle } from "lucide-react";
import { MDBContainer } from "mdb-react-ui-kit";
import { api } from "../../../services";
import { Tag, DataTable } from "../../ui";
import "./ActivityLogs.css";
import Toolbar from "../../Toolbar";

const ActivityLogs = () => {
  const { t, i18n } = useTranslation("translation");
  const isRTL = i18n.language === "ar" || i18n.dir() === "rtl";
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState("All");
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState("");
  const [selectedServices, setSelectedServices] = useState([]);
  const deliveryOptions = ["Jahez", "Keeta", "Ninja", "The Chefz"];
  const [selectedOperations, setSelectedOperations] = useState([]);
  const operationOptions = [
    "order_created",
    "order_updated",
    "order_cancelled",
    "webhook_received",
    "webhook_processed",
  ];
  const [selectedOrderStatuses, setSelectedOrderStatuses] = useState([]);
  const orderStatusOptions = ["PENDING", "ACCEPTED", "DELIVERED"];
  const [selectedLogTypes, setSelectedLogTypes] = useState([]);
  const logTypeOptions = ["log", "error"];
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [tableSortColumn, setTableSortColumn] = useState("date");
  const [sortDirection, setSortDirection] = useState("desc");
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    total: 0,
    totalPages: 1,
  });
  const [exporting, setExporting] = useState(false);

  const statusOptions = [
    { id: "All", name: t("all") },
    { id: "Success", name: t("success") },
    { id: "Failure", name: t("failure") },
    { id: "Pending", name: t("pending") },
  ];

  const fetchLogs = async () => {
    setLoading(true);
    setError(null);
    try {
      const params = {
        page: pagination.page,
        limit: pagination.limit,
        search: debouncedSearchQuery,
      };
      if (selectedServices.length > 0) {
        params.delivery_service = selectedServices.join(",");
      }
      if (selectedOperations.length > 0) {
        params.operation_type = selectedOperations.join(",");
      }
      const statusSet = new Set();
      if (selectedOrderStatuses.length > 0) {
        selectedOrderStatuses.forEach((s) => statusSet.add(s || ""));
      }
      if (activeTab && activeTab !== "All") {
        const tabVal =
          activeTab === "Failure" ? "failed" : activeTab.toLowerCase();
        statusSet.add(tabVal);
      }
      if (statusSet.size > 0) {
        params.status = Array.from(statusSet).join(",");
      }
      if (selectedLogTypes.length > 0) {
        params.log_type = selectedLogTypes.join(",");
      }
      if (startDate) {
        params.start_date = startDate;
      }
      if (endDate) {
        params.end_date = endDate;
      }
      const token = localStorage.getItem("adminToken");
      const response = await api.get("/system-logs", {
        params,
        headers: { Authorization: `Bearer ${token}` },
      });
      const incoming = response.data?.dashboard ?? response.data?.data ?? [];
      setLogs(incoming || []);
      setPagination(response.data?.pagination ?? pagination);
    } catch (err) {
      setError(`${t("Failed to fetch logs. Please try again later.")}`);
      console.error("Error fetching activity logs:", err);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    const date = new Date(dateString);
    return date.toLocaleString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  useEffect(() => {
    fetchLogs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    pagination.page,
    pagination.limit,
    activeTab,
    debouncedSearchQuery,
    selectedServices,
    selectedOperations,
    selectedOrderStatuses,
    selectedLogTypes,
    startDate,
    endDate,
  ]);

  useEffect(() => {
    const handler = setTimeout(() => setDebouncedSearchQuery(searchQuery), 400);
    return () => clearTimeout(handler);
  }, [searchQuery]);

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    setPagination((prev) => ({ ...prev, page: 1 }));
  };

  const handleSearchChange = (value) => {
    setSearchQuery(value);
    setPagination((prev) => ({ ...prev, page: 1 }));
  };

  const handlePageChange = (page) => {
    if (page >= 1 && page <= pagination.totalPages) {
      setPagination((prev) => ({ ...prev, page }));
    }
  };

  const getFilenameFromHeader = (header) => {
    if (!header) return null;
    const match = /filename\*=UTF-8''([^;]+)|filename="?([^";]+)"?/i.exec(
      header
    );
    if (!match) return null;
    const name = match[1] || match[2];
    try {
      return decodeURIComponent(name);
    } catch {
      return name;
    }
  };

  const handleExportClick = async () => {
    try {
      setExporting(true);
      const params = {
        search: debouncedSearchQuery,
      };
      if (selectedServices.length > 0) {
        params.delivery_service = selectedServices.join(",");
      }
      if (selectedOperations.length > 0) {
        params.operation_type = selectedOperations.join(",");
      }
      const statusSet = new Set();
      if (selectedOrderStatuses.length > 0) {
        selectedOrderStatuses.forEach((s) => statusSet.add(s || ""));
      }
      if (activeTab && activeTab !== "All") {
        const tabVal =
          activeTab === "Failure" ? "failed" : activeTab.toLowerCase();
        statusSet.add(tabVal);
      }
      if (statusSet.size > 0) {
        params.status = Array.from(statusSet).join(",");
      }
      if (selectedLogTypes.length > 0) {
        params.log_type = selectedLogTypes.join(",");
      }
      if (startDate) {
        params.start_date = startDate;
      }
      if (endDate) {
        params.end_date = endDate;
      }

      const token = localStorage.getItem("adminToken");
      const response = await api.get("/system-logs/export", {
        params,
        responseType: "blob",
        headers: { Authorization: `Bearer ${token}` },
      });
      const contentType =
        response.headers["content-type"] || "application/octet-stream";
      const blob = new Blob([response.data], { type: contentType });
      const url = URL.createObjectURL(blob);
      const cd = response.headers["content-disposition"];
      let filename =
        getFilenameFromHeader(cd) ||
        (contentType.includes("csv")
          ? "system-logs.csv"
          : contentType.includes("json")
            ? "system-logs.json"
            : "system-logs.bin");
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error("Export failed", err);
    }
    setExporting(false);
  };

  const getStatusVariant = (status) => {
    if (status === "Success") return "success";
    if (status === "Failure") return "error";
    return "warning";
  };

  const capitalizeStatus = (status) => {
    if (!status) return "N/A";
    if (status === "success") return "Success";
    if (status === "failed") return "Failure";
    if (status === "pending") return "Pending";
    return status.charAt(0).toUpperCase() + status.slice(1);
  };

  const columns = useMemo(
    () => [
      {
        id: "user",
        header: t("user"),
        sortable: true,
        render: (log) => log.user?.name || "N/A",
        className: "td-user",
      },
      {
        id: "store",
        header: t("store"),
        sortable: true,
        render: (log) => log.store?.name || "N/A",
        className: "td-store",
      },
      {
        id: "branch",
        header: t("branch"),
        sortable: true,
        render: (log) => log.branch?.name || "N/A",
        className: "td-branch",
      },
      {
        id: "deliveryService",
        header: t("deliveryService"),
        sortable: true,
        render: (log) => log.delivery_service || "N/A",
        className: "td-delivery",
      },
      {
        id: "date",
        header: t("date"),
        sortable: true,
        render: (log) => formatDate(log.date || log.datetime),
        className: "td-date",
      },
      {
        id: "status",
        header: t("status"),
        sortable: true,
        render: (log) => (
          <Tag variant={getStatusVariant(log.operation_type)}>
            {capitalizeStatus(log.operation_type)}
          </Tag>
        ),
        className: "td-status",
      },
      {
        id: "operationType",
        header: t("operationType"),
        sortable: true,
        render: (log) => log.status || "N/A",
        className: "td-operation",
      },
      {
        id: "details",
        header: t("details"),
        render: (log) => log.details || log.log_details || "N/A",
        className: "td-details",
      },
    ],
    [t]
  );

  const sortedLogs = useMemo(() => {
    const list = [...logs];
    const dir = sortDirection === "asc" ? 1 : -1;
    const getVal = (log) => {
      switch (tableSortColumn) {
        case "user":
          return (log.user?.name || "").toLowerCase();
        case "store":
          return (log.store?.name || "").toLowerCase();
        case "branch":
          return (log.branch?.name || "").toLowerCase();
        case "deliveryService":
          return (log.delivery_service || "").toLowerCase();
        case "date":
          return log.date || log.datetime
            ? new Date(log.date || log.datetime).getTime()
            : 0;
        case "status":
          return (log.operation_type || "").toLowerCase();
        case "operationType":
          return (log.status || "").toLowerCase();
        default:
          return "";
      }
    };
    list.sort((a, b) => {
      const av = getVal(a);
      const bv = getVal(b);
      if (typeof av === "number" && typeof bv === "number") {
        return dir * (av - bv);
      }
      return dir * String(av).localeCompare(String(bv));
    });
    return list;
  }, [logs, tableSortColumn, sortDirection]);

  return (
    <div className="admin-page-root oh-page activity-logs-page">
      <MDBContainer fluid className="content-area flex-grow-1">
        <h1 className="page-title-header">{t("activitylogs")}</h1>

        <div className="oh-page-section">
          <Toolbar
            showFilter
            orderStatuses={statusOptions}
            activeTab={activeTab}
            setActiveTab={handleTabChange}
            showSearch
            searchPlaceHolder={t("Search logs...")}
            searchQuery={searchQuery}
            handleSearch={(e) => handleSearchChange(e?.target?.value ?? e)}
            showDateRangeForActivity
            startDate={startDate}
            endDate={endDate}
            onStartDateChange={(d) => {
              setStartDate(d);
              setPagination((prev) => ({ ...prev, page: 1 }));
            }}
            onEndDateChange={(d) => {
              setEndDate(d);
              setPagination((prev) => ({ ...prev, page: 1 }));
            }}
            showDeliveryFilter
            deliveryOptions={deliveryOptions}
            selectedServices={selectedServices}
            onChangeSelectedServices={(services) => {
              setSelectedServices(services);
              setPagination((prev) => ({ ...prev, page: 1 }));
            }}
            showOperationFilter
            operationOptions={operationOptions}
            selectedOperations={selectedOperations}
            onChangeSelectedOperations={(ops) => {
              setSelectedOperations(ops);
              setPagination((prev) => ({ ...prev, page: 1 }));
            }}
            showOrderStatusFilter
            orderStatusOptions={orderStatusOptions}
            selectedOrderStatuses={selectedOrderStatuses}
            onChangeSelectedOrderStatuses={(sts) => {
              setSelectedOrderStatuses(sts);
              setPagination((prev) => ({ ...prev, page: 1 }));
            }}
            showLogTypeFilter
            logTypeOptions={logTypeOptions}
            selectedLogTypes={selectedLogTypes}
            onChangeSelectedLogTypes={(lts) => {
              setSelectedLogTypes(lts);
              setPagination((prev) => ({ ...prev, page: 1 }));
            }}
          />
        </div>

        <div className="oh-page-section">
          <DataTable
            tableId="activity-logs"
            title={t("activitylogs")}
            columns={columns}
            data={error ? [] : sortedLogs}
            rowKey="_id"
            loading={loading}
            emptyState={error || t("No logs found")}
            isRtl={isRTL}
            sort={{
              column: tableSortColumn,
              direction: sortDirection,
              onChange: ({ column, direction }) => {
                setTableSortColumn(column);
                setSortDirection(direction);
              },
            }}
            primaryAction={{
              label: t("export"),
              onClick: handleExportClick,
              disabled: exporting,
              icon: exporting ? (
                <LoaderCircle size={18} className="spin" />
              ) : (
                <Download size={18} />
              ),
            }}
            pagination={{
              page: pagination.page,
              pageSize: pagination.limit,
              total: pagination.total,
              onPageChange: handlePageChange,
              onPageSizeChange: (size) => {
                setPagination((prev) => ({ ...prev, limit: size, page: 1 }));
              },
              pageSizeOptions: [10, 20, 50],
              showLabel: t("Show", { defaultValue: "Show" }),
              ofLabel: t("of", { defaultValue: "Of" }),
              resultsLabel: t("results", { defaultValue: "results" }),
              isRtl: isRTL,
              previousLabel: t("previous"),
              nextLabel: t("next"),
            }}
          />
        </div>
      </MDBContainer>
    </div>
  );
};

export default ActivityLogs;
